import React, { useEffect } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Dashboard } from './pages/Dashboard';
import { NewSplitter } from './pages/NewSplitter';
import { GasEstimator } from './pages/GasEstimator';
import { assetUrl, brand } from './config/brand';
// Non-EVM wallet buttons hidden until Solana/Sui deploy — restore:
// import { SolanaConnectButton } from './components/SolanaConnectButton';
// import { SuiConnectButton } from './components/SuiConnectButton';
import './App.css';

function Layout({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    document.title = brand.htmlTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', brand.metaDescription);
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
      themeColor.setAttribute('content', '#000000');
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-base-100 text-base-content" data-theme={brand.daisyTheme}>
      <header className="border-b border-neutral-700/80 bg-base-100/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded">
            <img
              src={assetUrl(brand.logoHeaderSrc)}
              alt={brand.logoHeaderAlt}
              className="h-8 w-auto max-w-[min(100vw-8rem,14rem)]"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-4 text-sm">
            <Link to="/" className="link link-hover opacity-90">
              Dashboard
            </Link>
            <Link to="/new" className="link link-hover opacity-90">
              Create split
            </Link>
            <Link to="/gas" className="link link-hover opacity-90">
              Gas estimator
            </Link>
            <a
              href={brand.nav.secondaryHref}
              target="_blank"
              rel="noopener noreferrer"
              className="link link-hover opacity-90"
            >
              {brand.nav.secondaryLabel}
            </a>
            <div className="flex items-center gap-1 border-l border-neutral-700/60 pl-3 ml-1">
              <ConnectButton showBalance={false} chainStatus="icon" />
              {/* <SolanaConnectButton /> */}
              {/* <SuiConnectButton /> */}
            </div>
          </nav>
          <div className="dropdown dropdown-end md:hidden">
            <label tabIndex={0} className="btn btn-ghost btn-square" aria-label="Open navigation menu">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </label>
            <div tabIndex={0} className="dropdown-content mt-2 w-56 rounded-box border border-neutral-700/80 bg-base-100 p-3 shadow-lg">
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/" className="btn btn-ghost justify-start">
                  Dashboard
                </Link>
                <Link to="/new" className="btn btn-ghost justify-start">
                  Create split
                </Link>
                <Link to="/gas" className="btn btn-ghost justify-start">
                  Gas estimator
                </Link>
                <a
                  href={brand.nav.secondaryHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost justify-start"
                >
                  {brand.nav.secondaryLabel}
                </a>
                <div className="pt-2 border-t border-neutral-700/80 flex flex-col gap-2">
                  <ConnectButton showBalance={false} chainStatus="icon" />
                  {/* <SolanaConnectButton /> */}
                  {/* <SuiConnectButton /> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-neutral-700/80 py-6 text-center text-xs opacity-50">
        {brand.footer.copyright} ·{' '}
        <a
          href={brand.footer.linkHref}
          target="_blank"
          rel="noopener noreferrer"
          className="link link-hover"
        >
          {brand.footer.linkLabel}
        </a>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/new" element={<NewSplitter />} />
        <Route path="/gas" element={<GasEstimator />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
